import type { HTMLAttributes } from "react";
import { cn } from "@/lib/utils/cn";

// No blue anywhere in the palette (2026-08-13 rebrand) - "teal" fills the
// role the "blue" tone used to (informational/in-progress, distinct from
// green=success, amber=pending, indigo=locked/special), since teal reads as
// its own colour rather than a shade of the banned blue. "indigo" replaces
// what used to be "purple" (2026-08-17 rebrand) - the app's new `primary`
// brand colour is itself violet/purple, so the old purple status tone was
// repointed to indigo to stay visually distinct from primary-colored
// buttons/nav rather than blending into them.
//
// "purple"/"pink" (2026-08-18, Dynamic-Element Accent pass) are new,
// separate from "indigo" - deliberately NOT reused for any existing
// STATUS_TONE entry (confirmed live: Pending stays amber everywhere,
// unchanged) so the pre-existing indigo-avoids-primary-purple reasoning
// above still holds. These two exist purely as additive vocabulary for
// call sites with no good existing tone to reach for (e.g. a genuinely new
// "special/attention" indicator), not a recoloring of anything that
// already has a working, consistent semantic.
// "orange" (2026-09-12) is deliberately its own tone rather than a reuse of
// "amber": the attendance Flags column already renders Late and Early exit in
// amber, and "Missing checkout" is a different KIND of signal - the record is
// incomplete and needs a correction, not merely a punctuality note - so it has
// to be distinguishable at a glance in a cell that can show several badges at
// once.
type Tone = "slate" | "green" | "amber" | "orange" | "red" | "teal" | "indigo" | "purple" | "pink";

const toneClasses: Record<Tone, string> = {
  slate: "bg-slate-100 text-slate-700",
  green: "bg-emerald-100 text-emerald-800",
  amber: "bg-amber-100 text-amber-800",
  orange: "bg-orange-100 text-orange-800",
  red: "bg-red-100 text-red-700",
  teal: "bg-teal-100 text-teal-800",
  indigo: "bg-indigo-100 text-indigo-700",
  purple: "bg-primary-100 text-primary-800",
  pink: "bg-accent-100 text-accent-800",
};

interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: Tone;
}

export function Badge({ className, tone = "slate", ...props }: BadgeProps) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize",
        toneClasses[tone],
        className,
      )}
      {...props}
    />
  );
}

/** Maps the backend's status enums onto a consistent colour so "locked" always
 * reads as a warning and "paid"/"active"/"approved" always reads as success,
 * regardless of which resource the badge is decorating. */
const STATUS_TONE: Record<string, Tone> = {
  active: "green",
  approved: "green",
  present: "green",
  completed: "green",
  succeeded: "green",
  paid: "green",
  processed: "green",

  pending: "amber",
  pending_manager: "amber",
  pending_hr: "amber",
  pending_approval: "amber",
  pending_final_approval: "amber",
  hr_approved: "green",
  half_day: "amber",
  processing: "amber",
  probation: "amber",
  notice_period: "amber",
  partial: "amber",
  on_hold: "amber",
  trialing: "amber",
  past_due: "amber",
  open: "amber",
  filed: "amber",
  submitted: "amber",
  // ---- payroll approval workflow (Phase 2) ----
  // "submitted"/"approved"/"rejected" already have tones above (shared).
  // "review" is the only genuinely new payroll-run status needing one.
  review: "teal",
  generated: "teal",
  verified: "green",
  notified: "teal",

  locked: "indigo",
  draft: "slate",
  not_marked: "slate",
  inactive: "slate",
  weekly_off: "slate",
  week_off_worked: "amber",
  holiday: "teal",
  leave: "teal",
  on_duty: "teal",
  incomplete: "slate",

  absent: "red",
  rejected: "red",
  manager_rejected: "red",
  hr_rejected: "red",
  failed: "red",
  suspended: "red",
  terminated: "red",
  cancelled: "red",
  canceled: "red",
  uncollectible: "red",
  resigned: "slate",
  retired: "slate",
  void: "slate",
  revoked: "red",

  // ---- document management ----
  pending_verification: "amber",
  uploaded: "teal",
  overdue: "red",
  review_overdue: "red",
  expired: "red",
  archived: "slate",
  // client-derived compliance tri-state (Document Compliance Tracker) - no
  // raw backend enum for this, it's computed from the per-employee summary.
  complete: "green",
  action_needed: "red",

  // ---- biometric punch processing ----
  duplicate: "amber",

  // ---- call analytics ----
  // "rejected"/"failed" already have tones above (shared).
  answered: "green",
  missed: "amber",

  // ---- HR helpdesk ----
  // "assigned"/"in_progress"/"open"/"closed" already have tones above
  // (shared with other modules); these two are helpdesk-specific.
  waiting_for_employee: "indigo",
  resolved: "green",

  // ---- HR helpdesk Phase 3: SLA status ----
  // "active"/"not_started"/"resolved" already have tones above (shared).
  responded: "teal",
  breached: "red",

  // ---- talent: recruitment ----
  hiring: "teal",
  filled: "green",
  applied: "slate",
  screening: "amber",
  shortlisted: "teal",
  interview: "teal",
  assessment: "teal",
  final_review: "amber",
  offer: "indigo",
  hired: "green",
  withdrawn: "slate",
  scheduled: "teal",
  rescheduled: "amber",
  no_show: "red",
  sent: "teal",
  accepted: "green",
  strong_hire: "green",
  hire: "green",
  hold: "amber",
  reject: "red",

  closed: "slate",
  maintenance: "amber",

  // ---- talent: asset management ----
  available: "green",
  assigned: "teal",
  transferred: "teal",
  returned: "slate",
  lost: "red",
  damaged: "red",
  requested: "amber",
  in_progress: "amber",
  returned_with_damage: "red",
  missing: "red",
  needs_maintenance: "amber",
  cleared: "green",
  cleared_with_override: "amber",
  ended: "slate",
  waived: "slate",
  new: "green",
  good: "green",
  fair: "amber",
  poor: "red",

  // ---- performance management ----
  not_started: "slate",
  self_review: "teal",
  manager_review: "amber",
  at_risk: "amber",
  under_review: "amber",
  extended: "amber",
  planned: "slate",

  // ---- employee lifecycle / separation / final settlement ----
  // "approved"/"rejected"/"cancelled"/"completed"/"paid"/"probation"/
  // "notice_period"/"terminated"/"resigned"/"retired"/"withdrawn" already
  // have tones above (shared).
  initiated: "amber",
  clearance: "amber",
  settlement: "teal",
  calculated: "teal",
  finalized: "indigo",
  confirmed: "green",

  // ---- overtime tracking and approval ----
  // "manager_rejected"/"hr_rejected"/"hr_approved"/"cancelled"/"draft"
  // already have tones above (shared with Leave/Salary Advance's identical
  // status names). Only the genuinely new ones need entries here.
  pending_manager_approval: "amber",
  manager_approved: "green",
  pending_hr_approval: "amber",
  payroll_processed: "green",

  // ---- payroll payment processing ----
  // "draft"/"processing"/"failed"/"completed"/"cancelled"/"pending" already
  // have tones above (shared). Only the genuinely new batch/item statuses
  // need entries here.
  ready: "teal",
  successful: "green",
  partially_failed: "amber",
  reconciled: "indigo",
};

export function StatusBadge({ status, className }: { status: string; className?: string }) {
  const tone = STATUS_TONE[status] ?? "slate";
  return (
    <Badge tone={tone} className={className}>
      {status.replace(/_/g, " ")}
    </Badge>
  );
}

/** Labels the providers whose punches are synthetic rather than read off real
 * hardware. Indigo is the platform's "simulated data" colour, so a demo
 * tenant's attendance is never mistaken for genuine device evidence at a
 * glance. Real vendors fall through to their raw provider name. */
const SIMULATED_PROVIDER_LABEL: Record<string, string> = {
  simulator: "Simulated",
  face_recognition: "Face (Simulated)",
};

export function ProviderBadge({ provider, className }: { provider: string; className?: string }) {
  const simulated = SIMULATED_PROVIDER_LABEL[provider];
  return (
    <Badge tone={simulated ? "indigo" : "slate"} className={className}>
      {simulated ?? provider}
    </Badge>
  );
}

/** Warns that a GPS fix was reported by the phone as mocked (a fake-GPS app
 * or developer mock location), so the position cannot be trusted. Red, like
 * every other "this evidence is not genuine" signal an admin must act on. */
export function MockLocationBadge({ className }: { className?: string }) {
  return (
    <Badge
      tone="red"
      className={className}
      title="The phone reported this position as mocked (fake GPS). It may not be where the employee really was."
      data-testid="mock-location-badge"
    >
      Mock location
    </Badge>
  );
}
